"use client";

import {
  BadgeCheck,
  Bell,
  ChevronsUpDown,
  CreditCard,
  LogOut,
  Sparkles,
} from "lucide-react";
import { useEffect } from "react";
import { usePathname, useRouter } from "next/navigation";
import { authService } from "@/services/auth-service";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "@/redux/features/auth-slice";
import { resetCommandState } from "@/redux/features/command";
import { RootState } from "@/redux/store";
import { getInitials } from "@/utils/global";
import { useLogout } from "@/hooks/use-logout";

export function NavUser() {
  const router = useRouter();
  const user = useSelector((state: RootState) => state.user.user);
  const userLoading = useSelector((state: RootState) => state.user.isLoading);
  const userInitials = getInitials(user?.fullname);

  const { isMobile } = useSidebar();
  const dispatch = useDispatch();
  const logoutMutation = useLogout();
  const pathname = usePathname();

  const handleLogout = async () => {
    const onSuperAdminApp =
      pathname.startsWith("/super-admin") && pathname !== "/super-admin/login";

    if (onSuperAdminApp) {
      await authService.logout().catch(() => undefined);
      dispatch(logout());
      router.push("/super-admin/login");
      return;
    }

    await logoutMutation.mutateAsync().catch(() => undefined);
    dispatch(logout());
    dispatch(resetCommandState());
    router.push("/login");
  };

  useEffect(() => {
    if (!userLoading) {
      const isSuperAdmin = user?.role?.title === "Super Admin";
      const isSuperAdminRoute = pathname.includes("super-admin");
      const isSuperAdminAuthPage = pathname === "/super-admin/login";

      if (isSuperAdmin && !isSuperAdminRoute) {
        handleLogout();
      } else if (
        user &&
        !isSuperAdmin &&
        isSuperAdminRoute &&
        !isSuperAdminAuthPage
      ) {
        handleLogout();
      }
    }
  }, [user, pathname, userLoading]);

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground md:h-8 md:p-0"
            >
              <Avatar className="h-8 w-8 rounded-lg">
                <AvatarImage
                  src={user?.profile_image_url}
                  alt={user?.fullname}
                />
                <AvatarFallback className="rounded-lg" suppressHydrationWarning>
                  {userInitials}
                </AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-semibold" suppressHydrationWarning>{user?.fullname}</span>
                <span className="truncate text-xs" suppressHydrationWarning>{user?.email}</span>
              </div>
              <ChevronsUpDown className="ml-auto size-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
            side={isMobile ? "bottom" : "right"}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <Avatar className="h-8 w-8 rounded-lg">
                  <AvatarImage
                    src={user?.profile_image_url}
                    alt={user?.fullname}
                  />
                  <AvatarFallback className="rounded-lg" suppressHydrationWarning>
                    {userInitials}
                  </AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold" suppressHydrationWarning>
                    {user?.fullname}
                  </span>
                  <span className="truncate text-xs" suppressHydrationWarning>{user?.email}</span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />

            <DropdownMenuItem onClick={handleLogout}>
              <LogOut className="size-4 mr-2" />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
